import qs from 'qs';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { FunctionComponent, ReactNode, RefObject } from 'react';
import type { InjectedRouter } from 'react-router';

import { window } from '../../../globals';
import { testPasswordStrength } from '../../../helpers/passwordStrength';
import { useAsyncStatus, useInputValue } from '../../../hooks';
import { hardRedirectTo } from '../../../utils';
import type { LoginNextResponse, LoginResponse } from '../../../../typings/api';
import type { PasswordStrength } from '../../../helpers/passwordStrength';
import type { AsyncStatus, RouterProps } from '../../../types';
import type { TextInputOnChange } from '../../generic/Forms/InputBase';
import type { AuthPage } from '../index';

import {
  createOrganization,
  getPageFromPathname,
  getSSOAlertText,
  isSubmitBtnDisabled,
  login,
  loginNext,
  register,
} from './helpers';
import useFocusInputs from './useFocusInputs';
import useGetLoginMethods from './useGetLoginMethods';
import useInviteToken from './useInviteToken';
import useLdapAuthRequired from './useLdapAuthRequired';
import useLoginError from './useLoginError';

export type AuthInput = {
  ref: RefObject<HTMLInputElement>;
  value: string;
  onChange: TextInputOnChange;
};

export type LoginStep = 'enterEmail' | 'authenticate';

export type AuthContextState = {
  //  Which "page" we are on, which determines the UI to display
  page: AuthPage;
  //  'enterEmail' is where users enter their email, 'authenticate' is what happens next.
  step: LoginStep;
  email: AuthInput;
  password: AuthInput;
  hidePasswordInput: boolean;
  isLdapAuthRequired: boolean;
  status: AsyncStatus;
  //  Displays the UI alerts
  alert: string | null;
  submitButton: {
    isDisabled: boolean;
    onSubmit: () => void;
  };
  loginMethods: {
    bitbucket?: boolean;
    github?: boolean;
    gitlab?: boolean;
    google?: boolean;
  };
  //  REGISTER-SPECIFIC
  hasInvite: boolean;
  orgTitle: string | null;
  inviteToken?: string;
  passwordStrength?: PasswordStrength;
  samlURL: string | null;
};

/**
 * Context allows us to store values (ie: state) and share it across components.
 * By creating this top-level state container, we can then either wrap children
 * in `<AuthContext.Consumer>`, or children can use the `useContext` hook,
 * which gives those components access to this state without needing to explicitly pass them as props.
 * Read more about React Context API here: https://reactjs.org/docs/context.html
 */
export const AuthContext = createContext<AuthContextState>({} as AuthContextState);

//  This is a hook which allows us to use this context in any component.
//  If the `value` in <AuthContextProvider /> ever changes, components
//  using this hook will be re-rendered with the updated state.
export const useAuthContext = () => useContext<AuthContextState>(AuthContext);

type AuthContextProviderProps = {
  children: ReactNode;
  location: RouterProps['location'];
  inviteToken?: string;
  router?: InjectedRouter; // Optional, since it only exists on the login page.
};

/**
 * Wrapper for `<AuthContext.Provider>`, which hits `/account/login_state` on mount,
 * then stores the results in useState which is passed into AuthContext.
 *
 * @param {ReactNode} children - Child component tree root which will be provided this context.
 * @param {RouterProps['location']} location - The current location object.
 * @param {string} [inviteToken] - The invite token that was given to the user to register.
 * @param {InjectedRouter} router - The React app router.
 */
const AuthContextProvider: FunctionComponent<AuthContextProviderProps> = ({
  children,
  location,
  inviteToken,
  router,
}) => {
  //  Note that since pathname won't change (without re-rendering the parent & re-running this hook), we don't need to store it with useState.
  const page = getPageFromPathname(location.pathname);
  //  States
  const [step, setStep] = useState<LoginStep>('enterEmail');
  const [alert, setAlert] = useState<string | null>(null);
  const [hidePasswordInput, setHidePasswordInput] = useState(false);
  const [email, onEmailChange] = useInputValue(location.query?.email || ''); // If there is an email in the query param, we can set it as the initial value.
  const [password, onPasswordChange] = useInputValue('');
  const { emailInputRef, passwordInputRef } = useFocusInputs();
  const { status, ...statusMethods } = useAsyncStatus();
  const [samlURL, setSamlURL] = useState<string | null>(null);

  //  LOGIN ONLY
  useLoginError(statusMethods.onError, location.query);
  const isLdapAuthRequired = useLdapAuthRequired(setAlert, location.query);
  const [loginMethods, onUpdateLoginMethods] = useGetLoginMethods(isLdapAuthRequired);

  //  REGISTER ONLY
  const { orgTitle, invitedEmail } = useInviteToken(setAlert, inviteToken);

  // Consider the user to have an invite only if we've fetched the invited email (which means the token is valid).
  const hasInvite = !!invitedEmail;

  useEffect(() => {
    if (invitedEmail) {
      onEmailChange(invitedEmail);
    }
  }, [invitedEmail, onEmailChange]);

  //  Handles what happens when a user is trying to log into an SSO-only org.
  const handleRequireSSO = (response: LoginNextResponse | LoginResponse) => {
    //  This won't ever happen, it's just to please TS
    if (response.action !== 'require_sso') {
      return;
    }
    const isGoogleSSO = response.method === 'google';
    const samlRedirectURL: string | null = response.method === 'saml'
      ? response.samlURL
      : null;

    if (response.method !== 'ldap') {
      setHidePasswordInput(true);
    }

    if (response.method) {
      setAlert(
        getSSOAlertText(isGoogleSSO),
      );
    }

    //  Handle SAML redirect if this is a loginNext request.
    if (samlRedirectURL) {
      return hardRedirectTo(samlRedirectURL); // eslint-disable-line consistent-return
    }

    statusMethods.onSuccess();
    //  Update the login methods
    onUpdateLoginMethods({
      github: false,
      gitlab: false,
      bitbucket: false,
      google: isGoogleSSO,
    });
  };

  const onLogin = async (loc: RouterProps['location']) => {
    statusMethods.onPending();
    try {
      const response: LoginResponse = await login({
        username: email,
        password,
        next: loc.query.next,
        subdomain: loc.query.subdomain,
      });
      switch (response.action) {
        case 'redirect':
        case 'logged_in':
          hardRedirectTo(response?.to ?? '');
          return;
        case 'require_sso':
          handleRequireSSO(response);
          break;
        default:
          break;
      }
      statusMethods.onSuccess();
    } catch (error) {
      statusMethods.onError(error);
    }
  };

  // Makes a request to GET /account/login/next and handles the response, used for both signing in and registering.
  const onLoginNextStep = async (loc: RouterProps['location']) => {
    //  Inform the UI that we are loading, remove any stale Alerts
    statusMethods.onPending();
    setAlert(null);
    try {
      const response: LoginNextResponse = await loginNext(
        email,
        loc.query.subdomain,
        loc.query.inviteToken,
      );
      switch (response.action) {
        case 'redirect':
          hardRedirectTo(response?.to ?? '/auth/register');
          return;
        case 'clear_subdomain':
        // Do not carry on to the next step.
          setAlert(response.message);
          statusMethods.onSuccess();
          // Clear out the subdomain query parameter.
          router.push(loc.pathname);
          return;
        case 'signup':
        // If we're signing in and the response is to sign up, redirect to the registration page. Otherwise reveal the
        // password field for the user to choose a password.
          if (page !== 'register') {
            const appendEmail = email ? `?${qs.stringify({ email })}` : '';
            hardRedirectTo(`/auth/register${appendEmail}`);
            return;
          }
          break;
        case 'require_sso':
          handleRequireSSO(response);
          break;
          //  For password/non-fancy login users, we just show the next step where they input their password.
        case 'password':
          if (response.samlURL) {
          //  Display a "Login with SAML button" if there is a samlURL.
            setSamlURL(response.samlURL);
          }
          break;
        default:
          break;
      }
      setStep('authenticate');
      statusMethods.onSuccess();
    } catch (error) {
      statusMethods.onError(error);
    }
  };

  const onRegister = async () => {
    statusMethods.onPending();
    try {
      const response = await register({
        email,
        password,
        inviteToken,
      });
      let redirectTo = '/';
      if (!window.on_prem && !response.data?.organizationId) {
        // If not on prem and user is not already part of an organization, then create a new organization with the email as name
        await createOrganization(email, email);
        redirectTo = '/projects/import';
      }

      statusMethods.onSuccess();
      //  Server will log the user in, we just redirect them to the next page.
      hardRedirectTo(redirectTo);
    } catch (error) {
      statusMethods.onError(error);
    }
  };

  //  Handle submit button click
  const onSubmitButtonClick = useCallback(() => {
    if (page === 'login') {
      if (step === 'enterEmail') {
        return onLoginNextStep(location);
      }
      return onLogin(location);
    }
    //  Register handle next button click
    if (step === 'enterEmail') {
      return onLoginNextStep(location);
    }
    return onRegister();
  }, [
    //  Note that we need this callback to be refreshed if email/password are used, so that the login helpers get the updated values.
    page,
    step,
    email,
    password,
    location,
  ]);

  //  useMemo can help us avoid needless re-renders. This says "only update if one of these dependencies changes".
  const value: AuthContextState = useMemo(() => ({
    page,
    step,
    alert,
    email: {
      ref: emailInputRef,
      value: email,
      onChange: onEmailChange,
    },
    password: {
      ref: passwordInputRef,
      value: password,
      onChange: onPasswordChange,
    },
    hidePasswordInput,
    isLdapAuthRequired,
    submitButton: {
      isDisabled: isSubmitBtnDisabled({
        step,
        email,
        password,
        hidePasswordInput,
      }),
      onSubmit: onSubmitButtonClick,
    },
    status,
    loginMethods,
    hasInvite,
    orgTitle,
    inviteToken,
    passwordStrength: page === 'register'
      ? testPasswordStrength(password)
      : undefined, // No need to run password through the strength meter on the login page.
    samlURL,
  }), [
    page,
    step,
    alert,
    email,
    password,
    hidePasswordInput,
    status,
    loginMethods,
    onSubmitButtonClick,
    hasInvite,
    orgTitle,
    inviteToken,
    emailInputRef,
    passwordInputRef,
    samlURL,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;
